import type { BeautyPresetId } from '@/lib/beautyPresets';
import { BEAUTY_PRESETS } from '@/lib/beautyPresets';

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (!url.startsWith('data:') && !url.startsWith('blob:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

/** Apply beauty-like adjustments to a raster URL (returns PNG data URL). */
export async function applyClientBeautyToUrl(
  url: string,
  preset: BeautyPresetId
): Promise<string> {
  const values = BEAUTY_PRESETS[preset].values;
  const img = await loadImage(url);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas unsupported');

  const brightness = 1 + (values.brightness ?? 0);
  const contrast = 1 + (values.contrast ?? 0);
  const saturate = 1 + (values.saturation ?? 0);
  const blur = Math.max(0, (values.blur ?? 0) * 2);

  ctx.filter = [
    `brightness(${brightness})`,
    `contrast(${contrast})`,
    `saturate(${saturate})`,
    blur > 0.01 ? `blur(${blur}px)` : '',
  ]
    .filter(Boolean)
    .join(' ');
  ctx.drawImage(img, 0, 0, w, h);
  return c.toDataURL('image/png');
}
